import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cn } from "@/shared/utils/commonUtils"
import { Button } from "@/shared/ui/button"
import { useStoreShallow } from "@/stores"
import type { SidebarState } from "@/stores/types"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/tooltip"

// ---------------------------------------------
// Sidebar Hook (Zustand-based)
// ---------------------------------------------

/**
 * Hook to access sidebar state and actions.
 * Uses Zustand store instead of React Context for better performance.
 */
export function useSidebar() {
  return useStoreShallow((s) => ({
    state: s.sidebarState,
    toggleSidebar: s.toggleSidebar,
    isMobile: s.isMobile,
  }))
}

/**
 * @deprecated Use layout without SidebarProvider - Zustand handles state now.
 * Kept for backwards compatibility during migration.
 */
export interface SidebarProviderProps {
  defaultState?: SidebarState
  children: React.ReactNode
}

/**
 * @deprecated SidebarProvider is no longer needed. 
 * Zustand handles sidebar state. Just render children directly.
 */
export function SidebarProvider({ children }: SidebarProviderProps) {
  // Just render children - Zustand handles state now
  return (
    <div className="relative flex h-screen w-full bg-background text-foreground">
      {children}
    </div>
  )
}

// ---------------------------------------------
// Primitives
// ---------------------------------------------

export interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> {
  collapsible?: "icon" | "none"
}

export const Sidebar = React.forwardRef<HTMLDivElement, SidebarProps>(
  ({ className, collapsible = "icon", children, ...props }, ref) => {
    const { state } = useSidebar()
    const isCollapsed = collapsible === "icon" && state === "collapsed"

    return (
      <aside
        ref={ref}
        data-collapsible={collapsible}
        data-state={state}
        className={cn(
          "group/sidebar-wrapper bg-sidebar text-sidebar-foreground flex flex-col transition-[width] duration-500 ease-[cubic-bezier(0.19,1,0.22,1)] will-change-[width]",
          isCollapsed ? "w-[85px]" : "w-[240px]",
          className,
        )}
        {...props}
      >
        {children}
      </aside>
    )
  },
)
Sidebar.displayName = "Sidebar"

export const SidebarHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "border-b border-sidebar-border px-3 py-2 flex items-center justify-between gap-2 h-14",
        className,
      )}
      {...props}
    />
  ),
)
SidebarHeader.displayName = "SidebarHeader"

export const SidebarContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("flex-1 overflow-y-auto px-2 py-3 space-y-2", className)}
      {...props}
    />
  ),
)
SidebarContent.displayName = "SidebarContent"

export const SidebarFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("border-t border-sidebar-border px-2 py-2", className)}
      {...props}
    />
  ),
)
SidebarFooter.displayName = "SidebarFooter"

export const SidebarRail = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "pointer-events-none absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-sidebar-border/60 via-transparent to-sidebar-border/60 opacity-40 group-hover/sidebar-wrapper:opacity-100 transition-opacity",
        className,
      )}
      {...props}
    />
  ),
)
SidebarRail.displayName = "SidebarRail"

// Menu primitives

export const SidebarGroup = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("space-y-1", className)} {...props} />
  ),
)
SidebarGroup.displayName = "SidebarGroup"

export const SidebarGroupLabel = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("px-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground", className)}
      {...props}
    />
  ),
)
SidebarGroupLabel.displayName = "SidebarGroupLabel"

export const SidebarMenu = React.forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLUListElement>>(
  ({ className, ...props }, ref) => (
    <ul ref={ref} className={cn("space-y-1", className)} {...props} />
  ),
)
SidebarMenu.displayName = "SidebarMenu"

export const SidebarMenuItem = React.forwardRef<HTMLLIElement, React.LiHTMLAttributes<HTMLLIElement>>(
  ({ className, ...props }, ref) => (
    <li ref={ref} className={cn("list-none", className)} {...props} />
  ),
)
SidebarMenuItem.displayName = "SidebarMenuItem"

export interface SidebarMenuButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean
  tooltip?: string
  size?: "sm" | "md" | "lg" | "icon"
}

export const SidebarMenuButton = React.forwardRef<HTMLButtonElement, SidebarMenuButtonProps>(
  ({ className, size = "md", asChild, children, tooltip, ...props }, ref) => {
    const sizeClasses =
      size === "icon"
        ? "h-9 w-9 flex items-center justify-center"
        : size === "lg"
          ? "h-10 px-3"
          : size === "sm"
            ? "h-8 px-2"
            : "h-9 px-3"

    const baseClasses = cn(
      "inline-flex w-full items-center gap-2 rounded-lg text-sm font-medium transition-colors bg-transparent text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar",
      sizeClasses,
      className,
    )

    const Comp = asChild ? Slot : "button"

    const button = (
      <Comp
        ref={ref}
        className={baseClasses}
        {...props}
      >
        {children}
      </Comp>
    )

    if (!tooltip) {
      return button
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent side="right" align="center" hidden={false} variant="sidebar">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    )
  },
)
SidebarMenuButton.displayName = "SidebarMenuButton"

export const SidebarMenuSub = React.forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLUListElement>>(
  ({ className, ...props }, ref) => (
    <ul
      ref={ref}
      className={cn(
        "mt-1 space-y-0.5 border-l border-sidebar-primary/40 ml-4 pl-4 transition-colors duration-300",
        className,
      )}
      {...props}
    />
  ),
)
SidebarMenuSub.displayName = "SidebarMenuSub"

export const SidebarMenuSubItem = React.forwardRef<HTMLLIElement, React.LiHTMLAttributes<HTMLLIElement>>(
  ({ className, ...props }, ref) => (
    <li ref={ref} className={cn("list-none", className)} {...props} />
  ),
)
SidebarMenuSubItem.displayName = "SidebarMenuSubItem"

export interface SidebarMenuSubButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean
}

export const SidebarMenuSubButton = React.forwardRef<HTMLButtonElement, SidebarMenuSubButtonProps>(
  ({ className, asChild, children, ...props }, ref) => {
    const baseClasses = cn(
      "inline-flex w-full items-center rounded-md px-2 py-1.5 text-sm text-sidebar-foreground/70 hover:bg-sidebar-hover hover:text-sidebar-foreground transition-colors",
      className,
    )

    const Comp = asChild ? Slot : "button"

    return (
      <Comp
        ref={ref}
        className={baseClasses}
        {...props}
      >
        {children}
      </Comp>
    )
  },
)
SidebarMenuSubButton.displayName = "SidebarMenuSubButton"

// ---------------------------------------------
// Layout helpers (Trigger + Inset)
// ---------------------------------------------

export interface SidebarTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> { }

export const SidebarTrigger = React.forwardRef<HTMLButtonElement, SidebarTriggerProps>(
  ({ className, children, ...props }, ref) => {
    const { toggleSidebar } = useSidebar()

    return (
      <Button
        ref={ref}
        type="button"
        variant="outline"
        size="icon"
        onClick={toggleSidebar}
        className={cn(
          "inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-background text-foreground shadow-sm hover:bg-accent hover:text-accent-foreground",
          className,
        )}
        {...props}
      >
        {children ?? (
          <span className="sr-only">Toggle sidebar</span>
        )}
      </Button>
    )
  },
)
SidebarTrigger.displayName = "SidebarTrigger"

export const SidebarInset = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("flex min-h-screen flex-1 flex-col bg-background", className)}
      {...props}
    />
  ),
)
SidebarInset.displayName = "SidebarInset"
